import { useState, useCallback, useEffect } from 'react';
import AppLayout from '@amzn/awsui-components-react/polaris/app-layout';
import ContentLayout from "@amzn/awsui-components-console/content-layout";
import Container from "@amzn/awsui-components-console/container";
import { Header, Flashbar } from '@amzn/awsui-components-react';
import Button from '@amzn/awsui-components-react/polaris/button';
import Box from '@amzn/awsui-components-console/box';
import Toggle from '@amzn/awsui-components-react/polaris/toggle';
import Spinner from '@amzn/awsui-components-react/polaris/spinner';

import NavigationBreadcrumbs from "../../common/navigation/NavigationBreadcrumbs";
import SideNavigationMenu from '../../common/navigation/SideNavigationMenu';
import ChatbotContent from '../../components/ChatbotWidget/ChatbotContent';
import ConversationHistory from '../../components/ChatbotWidget/ConversationHistory/ConversationHistory';
import Paths from "../../common/paths/paths";
import { getString } from '../../common/ui-string-labels/ui-strings-utils';
import { useChatbot } from '../../context/chatbot/ChatbotContext';
import { useFlashBarItems } from '../../common/UseFlashBarItems/useFlashBarItems';

// Layout constants — derived from page header + breadcrumbs + container chrome
const HISTORY_PANEL_WIDTH = '300px';
const HISTORY_PANEL_HEIGHT = 'calc(100vh - 200px)';
const HISTORY_CONTENT_HEIGHT = 'calc(100vh - 240px)';
const HISTORY_PANEL_KEY = "chatbot-history-panel-open";

const breadcrumbs = {
    items:
    [
        { text: Paths.Admin.name, href: Paths.manageOrders.url },
        { text: Paths.chatbot.name, href: Paths.chatbot.url }
    ]
}

const ChatbotPage = () => {
    const [navigationOpen, setNavigationOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(() => {
        const stored = sessionStorage.getItem(HISTORY_PANEL_KEY);
        return stored !== null ? stored === "true" : true;
    });

    const flashBarItemsHook = useFlashBarItems();

    const {
        conversations,
        activeConversationId,
        conversationsLoading,
        conversationsInitializing,
        hasMoreConversations,
        historyAvailable,
        pendingConversationIds,
        unreadConversationIds,
        loadConversation,
        deleteConversation,
        startNewConversation,
        loadMoreConversations,
        initError,
        quickActionsLoading,
        quickActionsVisible,
        toggleQuickActions,
    } = useChatbot();

    const pageLoading = conversationsInitializing || quickActionsLoading;

    const handleDelete = async (id: string): Promise<boolean> => {
        const success = await deleteConversation(id);
        if (!success) {
            flashBarItemsHook.actions.addErrorFlashBarItem(getString('chatbot.errors.deleteConversation'));
        }
        return success;
    };

    // flashBarItemsHook.actions is intentionally omitted from the deps: useFlashBarItems returns a
    // fresh actions object every render, so including it would re-fire this effect on each render
    // and loop infinitely once initError is set (addErrorFlashBarItem -> re-render -> new identity).
    useEffect(() => {
        if (initError) {
            flashBarItemsHook.actions.addErrorFlashBarItem(initError);
        }
    }, [initError]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        sessionStorage.setItem(HISTORY_PANEL_KEY, String(historyOpen));
    }, [historyOpen]);

    const handleHistoryOpen = useCallback(() => setHistoryOpen(true), []);
    const handleHistoryClose = useCallback(() => setHistoryOpen(false), []);

    return (
        <AppLayout
            headerSelector="#top-nav"
            toolsHide={true}
            content={
                <ContentLayout
                    header={
                        <Header
                            variant="h1"
                            data-testid="chatbot-page-header"
                            actions={
                                !pageLoading && (
                                    <Toggle
                                        checked={quickActionsVisible}
                                        onChange={() => toggleQuickActions()}
                                    >
                                        {getString('chatbot.quickActions.showToggle')}
                                    </Toggle>
                                )
                            }
                        >
                            {getString('chatbot.drawer.header')}
                        </Header>
                    }
                >
                    {pageLoading ? (
                        <Box textAlign="center" padding="xxl" data-testid="chatbot-page-spinner">
                            <Spinner size="large" />
                        </Box>
                    ) : (
                        <div style={{ display: 'flex' }}>
                            {historyAvailable && (
                                historyOpen ? (
                                    <div style={{ flexShrink: 0, width: HISTORY_PANEL_WIDTH, height: HISTORY_PANEL_HEIGHT, marginRight: '16px' }}>
                                        <Container>
                                            <div style={{ height: HISTORY_CONTENT_HEIGHT }}>
                                                <ConversationHistory
                                                    conversations={conversations}
                                                    activeConversationId={activeConversationId}
                                                    loading={conversationsLoading}
                                                    hasMore={hasMoreConversations}
                                                    pendingConversationIds={pendingConversationIds}
                                                    unreadConversationIds={unreadConversationIds}
                                                    onSelect={loadConversation}
                                                    onDelete={handleDelete}
                                                    onNewChat={startNewConversation}
                                                    onLoadMore={loadMoreConversations}
                                                    onClose={handleHistoryClose}
                                                />
                                            </div>
                                        </Container>
                                    </div>
                                ) : (
                                    <div style={{ flexShrink: 0, height: HISTORY_PANEL_HEIGHT, marginRight: '16px' }}>
                                        <Box padding="xs">
                                            <Button
                                                variant="normal"
                                                iconName="angle-right"
                                                onClick={handleHistoryOpen}
                                                ariaLabel={getString('chatbot.history.toggleHistory')}
                                            />
                                        </Box>
                                    </div>
                                )
                            )}
                            <div style={{ flex: 1 }}>
                                <Container>
                                    <ChatbotContent variant="page" />
                                </Container>
                            </div>
                        </div>
                    )}
                </ContentLayout>
            }
            breadcrumbs={<NavigationBreadcrumbs items={breadcrumbs.items} />}
            navigation={<SideNavigationMenu />}
            navigationOpen={navigationOpen}
            notifications={<Flashbar items={flashBarItemsHook.flashBarItems} stackItems />}
            onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        />
    );
}

export default ChatbotPage;
